import { Router } from '@angular/router';
import { catchError, combineLatest, finalize, map, of, switchMap } from 'rxjs';

import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { DealerService } from '../services/dealer.service';
import { UserStore } from './user.store';
import { DealerInformation } from '../../shared/models/dealer-information.model';
import { UserInformation } from 'src/app/shared/models/user-information.model';

export type AppMode = 'pure' | 'battery-care';

@Injectable()
export class DealerStore {
  private dealerService = inject(DealerService);
  private userStore = inject(UserStore);
  private router = inject(Router);

  private _selectedDealerBpId = signal<string | undefined>(undefined);
  private _isLoading = signal<boolean>(true);
  private _appMode = signal<AppMode>(
    globalThis.location.pathname.includes('/batterycare') ? 'battery-care' : 'pure'
  );

  readonly appMode = this._appMode.asReadonly();

  setAppMode(mode: AppMode): void {
    this._appMode.set(mode);
  }

  readonly dealers = toSignal(
    combineLatest([
      toObservable(this.userStore.userInformation),
      toObservable(this._appMode),
    ]).pipe(
      switchMap(([userInformation, appMode]) => {
        if (!userInformation) {
          return of([] as DealerInformation[]);
        }
        const request$ = appMode === 'battery-care'
          ? this.dealerService.getBatteryCareDealers()
          : this.dealerService.getDealers();
        return request$.pipe(
          finalize(() => this._isLoading.set(false)),
          catchError((err) => {
            if (err.status === 403) {
              this.router.navigate(['/unauthorized']);
              return of([] as DealerInformation[]);
            }
            return of([] as DealerInformation[]);
          }),
          map((dealers) => dealers as DealerInformation[])
        );
      })
    )
  );

  readonly selectedDealer = computed(() => {
    const userInformation = untracked(this.userStore.userInformation) as UserInformation;
    const selectedDealerBpId = this._selectedDealerBpId() || userInformation.defaultBpId;
    return this.dealers()?.find((dealer) => dealer.bpId === selectedDealerBpId) as DealerInformation;
  });

  readonly isLoading = this._isLoading.asReadonly();

  readonly isValidationDealer = computed(() => this.selectedDealer()?.isValidationDealer ?? false);

  selectDealer(dealerId: string): void {
    this._selectedDealerBpId.set(dealerId);
  }

  getSelectedDealerBuno(): string {
    return this.selectedDealer().buno;
  }

  getSelectedDealerBpId(): string {
    return this.selectedDealer().bpId;
  }

  getSelectedDealerMarketCode(): string {
    return this.selectedDealer().isoCountryCode;
  }
}
